/* Centauri - Full background video with container. Progressive enhancement only.
   Replay stages can opt into viewport-owned playback: start on entry, pause offscreen,
   resume while unfinished, then return to the poster and expose replay at the end. */
(function () {
  'use strict';

  if (window.CentauriFBVC && typeof window.CentauriFBVC.init === 'function') {
    window.CentauriFBVC.init();
    return;
  }

  var instances = new WeakMap();
  var roots = [];
  var resizeTimer = null;

  function reduceMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function connectionAllowsAutoplay() {
    var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!connection) return true;
    if (connection.saveData) return false;
    var effectiveType = String(connection.effectiveType || '').toLowerCase();
    return effectiveType !== 'slow-2g' && effectiveType !== '2g';
  }

  function motionAutoplayAllowed() {
    return !reduceMotion() && connectionAllowsAutoplay();
  }

  function disableNativeAutoplay(videos) {
    for (var i = 0; i < videos.length; i++) {
      try {
        videos[i].removeAttribute('autoplay');
        videos[i].setAttribute('preload', 'none');
        videos[i].autoplay = false;
        videos[i].pause();
      } catch (e) {}
    }
  }

  function visibleVideo(videos) {
    for (var i = 0; i < videos.length; i++) {
      if (videos[i].offsetParent !== null) return videos[i];
    }
    return videos[0] || null;
  }

  function hydrateVideo(video) {
    if (!video) return false;
    // Single-clip markup bakes a real src (zero-JS native autoplay baseline).
    // Dual-clip markup declares each file through a per-breakpoint <source media>
    // child instead, so the browser has already picked a resource (currentSrc)
    // without an src attribute. The data-src branch covers engines that skipped
    // every <source> (off-breakpoint element, media-attr edge cases) and any
    // legacy deferred markup still around.
    if (video.getAttribute('src')) return true;
    if (video.currentSrc) return true;
    var source = video.getAttribute('data-src');
    if (source) {
      video.setAttribute('src', source);
      video.removeAttribute('data-src');
      try {
        video.load();
      } catch (e) {}
    }
    return !!video.getAttribute('src');
  }

  function showReplay(root, button, state) {
    root.classList.remove('is-playing');
    root.classList.remove('is-ended');
    root.classList.remove('is-error');
    root.classList.add(state === 'error' ? 'is-error' : 'is-ended');
    if (button) button.hidden = false;
  }

  function hideReplay(root, button) {
    root.classList.remove('is-ended');
    root.classList.remove('is-error');
    if (button) button.hidden = true;
  }

  function ignorePlaybackError(instance, error) {
    if (error && error.name === 'AbortError') return true;
    return instance.viewportAutoplay && !instance.isInView;
  }

  function handlePlaybackError(instance, video, error) {
    if (ignorePlaybackError(instance, error)) return;
    if (video && video !== visibleVideo(instance.videos)) return;
    showReplay(instance.root, instance.button, 'error');
  }

  function pauseOtherVideos(instance, activeVideo) {
    for (var i = 0; i < instance.videos.length; i++) {
      var video = instance.videos[i];
      if (video === activeVideo) continue;
      try {
        video.pause();
        if (instance.wantsReplay) video.currentTime = 0;
      } catch (e) {}
    }
  }

  function playFromStart(instance, video) {
    var root = instance.root;
    var button = instance.button;

    if (!video || !hydrateVideo(video)) {
      showReplay(root, button, 'error');
      return null;
    }

    // Force muted via the JS property (not just the markup attribute): when the
    // src is assigned programmatically, Safari/Chrome autoplay policies only honor
    // the muted *property*, so a bare `muted` attribute would get the play() rejected.
    try {
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
    } catch (e) {}

    hideReplay(root, button);
    pauseOtherVideos(instance, video);
    instance.currentVideo = video;

    try {
      video.currentTime = 0;
    } catch (e) {}

    var playback = null;
    try {
      playback = video.play();
    } catch (e) {
      handlePlaybackError(instance, video, e);
      return null;
    }

    if (playback && typeof playback.then === 'function') {
      playback.catch(function (error) {
        handlePlaybackError(instance, video, error);
      });
    }

    return playback || true;
  }

  function playViewportVideo(instance, video) {
    if (!video || video.ended) return null;

    var isFirstAttempt = !instance.hasAutoplayed;
    if (isFirstAttempt) instance.hasAutoplayed = true;

    var playback = isFirstAttempt
      ? playFromStart(instance, video)
      : playAmbient(instance, video);

    if (!playback) {
      if (isFirstAttempt) instance.hasAutoplayed = false;
      return null;
    }

    if (isFirstAttempt && typeof playback.catch === 'function') {
      playback.catch(function (error) {
        if (!ignorePlaybackError(instance, error) && video.paused && !video.ended) {
          instance.hasAutoplayed = false;
        }
      });
    }

    return playback;
  }

  function playAmbient(instance, video) {
    if (!video || !hydrateVideo(video)) return null;

    pauseOtherVideos(instance, video);
    instance.currentVideo = video;
    instance.root.classList.add('is-playing');

    var playback = null;
    try {
      playback = video.play();
    } catch (e) {
      if (instance.wantsReplay) handlePlaybackError(instance, video, e);
      else instance.root.classList.remove('is-playing');
      return null;
    }

    if (playback && typeof playback.catch === 'function') {
      playback.catch(function (error) {
        if (instance.wantsReplay) handlePlaybackError(instance, video, error);
        else instance.root.classList.remove('is-playing');
      });
    }

    return playback || true;
  }

  function pauseAmbient(instance) {
    for (var i = 0; i < instance.videos.length; i++) {
      try {
        instance.videos[i].pause();
      } catch (e) {}
    }
    instance.root.classList.remove('is-playing');
  }

  function addListener(instance, target, type, handler, options) {
    target.addEventListener(type, handler, options);
    instance.disposers.push(function () {
      target.removeEventListener(type, handler, options);
    });
  }

  function syncVisibleVideo(instance) {
    if (!instance || !instance.videos.length) return;

    var activeVideo = visibleVideo(instance.videos);
    if (!activeVideo) return;

    if (activeVideo !== instance.currentVideo) {
      pauseOtherVideos(instance, activeVideo);
      instance.currentVideo = activeVideo;

      if (
        instance.wantsReplay &&
        instance.hasAutoplayed &&
        instance.root.classList.contains('is-playing') &&
        activeVideo.paused
      ) {
        showReplay(instance.root, instance.button, 'ended');
      }

      if (
        !instance.wantsReplay &&
        instance.wantsAutoplay &&
        instance.isInView &&
        activeVideo.paused
      ) {
        playAmbient(instance, activeVideo);
      }

      if (
        instance.wantsReplay &&
        instance.viewportAutoplay &&
        instance.wantsAutoplay &&
        instance.isInView &&
        !document.hidden &&
        !activeVideo.ended
      ) {
        playViewportVideo(instance, activeVideo);
      }
    }
  }

  function syncAllVisibleVideos() {
    for (var i = 0; i < roots.length; i++) {
      syncVisibleVideo(instances.get(roots[i]));
    }
  }

  function scheduleVisibleSync() {
    if (resizeTimer) window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () {
      resizeTimer = null;
      syncAllVisibleVideos();
    }, 120);
  }

  function setup(root) {
    if (!root || instances.has(root)) return;

    var videos = [];
    var all = root.querySelectorAll('.centauri-fbvc__video');
    for (var i = 0; i < all.length; i++) {
      if (all[i].tagName === 'VIDEO') videos.push(all[i]);
    }

    var merchantWantsAutoplay = root.getAttribute('data-fbvc-autoplay') !== 'false';
    var forceAutoplay = root.hasAttribute('data-fbvc-force-autoplay');
    var instance = {
      root: root,
      videos: videos,
      button: root.querySelector('[data-video-replay]'),
      wantsReplay: root.hasAttribute('data-fbvc-replay'),
      viewportAutoplay: root.hasAttribute('data-fbvc-viewport-autoplay'),
      autoplayLead: root.hasAttribute('data-fbvc-autoplay-lead'),
      merchantWantsAutoplay: merchantWantsAutoplay,
      forceAutoplay: forceAutoplay,
      wantsAutoplay: merchantWantsAutoplay && (forceAutoplay || motionAutoplayAllowed()),
      hasAutoplayed: false,
      isInView: false,
      currentVideo: null,
      io: null,
      ioCenter: null,
      disposers: []
    };

    instances.set(root, instance);
    roots.push(root);
    root.classList.add('centauri-fbvc--js');

    if (!videos.length) return;
    if (!instance.wantsAutoplay) disableNativeAutoplay(videos);

    function syncAmbientVideoAttributes() {
      if (instance.wantsReplay) return;
      for (var v = 0; v < videos.length; v++) {
        try {
          videos[v].muted = true;
          videos[v].defaultMuted = true;
          videos[v].playsInline = true;
          // Never set .autoplay here: the autoplay flag makes Chrome ignore
          // preload="none" and pull the full stream for BOTH breakpoint variants
          // at setup time. The ambient IntersectionObserver below is the only
          // playback driver (2026-07-13 perf decision).
          videos[v].autoplay = false;
          if (videos[v].hasAttribute('loop')) videos[v].loop = true;
        } catch (e) {}
      }
    }

    function markReady(event) {
      if (event && event.currentTarget !== visibleVideo(videos)) return;
      instance.currentVideo = event ? event.currentTarget : visibleVideo(videos);
      pauseOtherVideos(instance, instance.currentVideo);
      root.classList.add('is-ready');
      root.classList.add('is-playing');
      if (instance.wantsReplay) hideReplay(root, instance.button);
    }

    for (var j = 0; j < videos.length; j++) {
      addListener(instance, videos[j], 'playing', markReady);
      addListener(instance, videos[j], 'play', function (event) {
        if (!instance.wantsReplay || event.currentTarget !== visibleVideo(videos)) return;
        // Any play (native autoplay included) consumes the one-shot budget so a
        // later IO callback never restarts a clip mid-flight.
        instance.hasAutoplayed = true;
        hideReplay(root, instance.button);
      });
    }

    if (!instance.wantsReplay) {
      // Autoplay normally respects motion/data preferences. Explicit ambient
      // modules such as C08 can opt in to force playback while preserving the
      // merchant autoplay switch.
      if (!instance.wantsAutoplay) {
        return;
      }

      syncAmbientVideoAttributes();

      function setAmbientInView(isInView) {
        instance.isInView = isInView;
        if (isInView) {
          playAmbient(instance, visibleVideo(videos));
        } else {
          pauseAmbient(instance);
        }
      }

      if ('IntersectionObserver' in window) {
        var ambientTarget = root.querySelector('.centauri-fbvc__nat') || root;
        instance.io = new IntersectionObserver(function (entries) {
          for (var n = 0; n < entries.length; n++) {
            setAmbientInView(entries[n].isIntersecting);
          }
        }, {
          threshold: [0],
          rootMargin: instance.autoplayLead ? '0px 0px 75% 0px' : '0px'
        });
        instance.io.observe(ambientTarget);
      } else {
        setAmbientInView(true);
      }

      return;
    }

    hideReplay(root, instance.button);

    // With a baked src the browser may have natively autoplayed (or finished)
    // before this script was rescued. Adopt that state instead of restarting.
    var nativeStarted = false;
    var nativeEnded = false;
    for (var p = 0; p < videos.length; p++) {
      try {
        if (!videos[p].paused || (videos[p].played && videos[p].played.length > 0)) {
          nativeStarted = true;
          if (videos[p].ended) nativeEnded = true;
        }
      } catch (e) {}
    }

    for (var k = 0; k < videos.length; k++) {
      try {
        // Keep the native `autoplay` attribute as a fallback to the JS path, and
        // force muted via the JS property so the browser's "muted video plays when
        // visible" policy (and Safari's dynamic-src case) is reliably satisfied.
        videos[k].muted = true;
        videos[k].defaultMuted = true;
        videos[k].playsInline = true;
        if (videos[k].paused && (!videos[k].played || videos[k].played.length === 0)) {
          videos[k].currentTime = 0;
        }
      } catch (e) {}

      addListener(instance, videos[k], 'ended', function (event) {
        if (event.currentTarget !== visibleVideo(videos)) return;
        showReplay(root, instance.button, 'ended');
      });

      addListener(instance, videos[k], 'error', function (event) {
        if (event.currentTarget !== visibleVideo(videos)) return;
        showReplay(root, instance.button, 'error');
      });
    }

    if (instance.button) {
      addListener(instance, instance.button, 'click', function () {
        var activeVideo = visibleVideo(videos);
        instance.button.classList.add('is-replaying');

        var playback = playFromStart(instance, activeVideo);
        if (!playback) {
          instance.button.classList.remove('is-replaying');
          return;
        }

        if (playback && typeof playback.then === 'function') {
          playback.then(function () {
            instance.button.classList.remove('is-replaying');
          }).catch(function (error) {
            instance.button.classList.remove('is-replaying');
            handlePlaybackError(instance, activeVideo, error);
          });
        } else {
          instance.button.classList.remove('is-replaying');
        }
      });
    }

    // One-shot autoplay is gated by user motion/data preferences. Reduced-motion
    // and save-data users see the poster and can opt in with the replay button.
    if (!instance.wantsAutoplay) {
      showReplay(root, instance.button, 'ended');
      return;
    }

    if (instance.viewportAutoplay) {
      if (nativeStarted) {
        instance.hasAutoplayed = true;
        if (nativeEnded) showReplay(root, instance.button, 'ended');
      }

      function setReplayInView(isInView) {
        instance.isInView = isInView;
        var activeVideo = visibleVideo(videos);

        if (!isInView || document.hidden) {
          pauseAmbient(instance);
          return;
        }

        if (!activeVideo || activeVideo.ended) return;

        playViewportVideo(instance, activeVideo);
      }

      if ('IntersectionObserver' in window) {
        var viewportTarget = root.querySelector('.centauri-fbvc__nat') || root;
        instance.io = new IntersectionObserver(function (entries) {
          for (var n = 0; n < entries.length; n++) {
            setReplayInView(entries[n].isIntersecting);
          }
        }, {
          threshold: [0],
          rootMargin: instance.autoplayLead ? '0px 0px 75% 0px' : '0px'
        });
        instance.io.observe(viewportTarget);
      } else {
        setReplayInView(true);
      }

      addListener(instance, document, 'visibilitychange', function () {
        setReplayInView(instance.isInView);
      });

      return;
    }

    if (nativeStarted) {
      instance.hasAutoplayed = true;
      if (nativeEnded) showReplay(root, instance.button, 'ended');
      return;
    }

    function autoplayOnce() {
      if (instance.hasAutoplayed) return;
      instance.hasAutoplayed = true;
      var activeVideo = visibleVideo(videos);
      // Native autoplay (baked src) may already be running when the gate trips;
      // restarting it from zero would read as a visible glitch.
      if (activeVideo && !activeVideo.paused && !activeVideo.ended) return;
      playFromStart(instance, activeVideo);
    }

    function stopAutoplayObservers() {
      if (instance.io) {
        instance.io.disconnect();
        instance.io = null;
      }
      if (instance.ioCenter) {
        instance.ioCenter.disconnect();
        instance.ioCenter = null;
      }
    }

    if ('IntersectionObserver' in window) {
      var target = root.querySelector('.centauri-fbvc__nat') || root;
      var fireAutoplay = function () {
        stopAutoplayObservers();
        autoplayOnce();
      };

      // Short clips: a plain ratio threshold works.
      instance.io = new IntersectionObserver(function (entries) {
        for (var n = 0; n < entries.length; n++) {
          if (entries[n].isIntersecting && entries[n].intersectionRatio >= 0.4) {
            fireAutoplay();
            break;
          }
        }
      }, { threshold: [0.4] });
      instance.io.observe(target);

      // Clips taller than ~2.5 viewports can never reach a 0.4 ratio, and a
      // "center inside 20-80%" check sampled at discrete thresholds is skipped
      // by fast scrolls. Shrinking the IO root to the middle 60% band makes any
      // overlap a guaranteed callback regardless of element height.
      instance.ioCenter = new IntersectionObserver(function (entries) {
        for (var n = 0; n < entries.length; n++) {
          if (entries[n].isIntersecting) {
            fireAutoplay();
            break;
          }
        }
      }, { rootMargin: '-20% 0px -20% 0px', threshold: 0 });
      instance.ioCenter.observe(target);
    } else {
      autoplayOnce();
    }
  }

  function destroyRoot(root) {
    var instance = instances.get(root);
    if (!instance) return;

    if (instance.io) instance.io.disconnect();
    if (instance.ioCenter) instance.ioCenter.disconnect();
    pauseAmbient(instance);
    for (var i = 0; i < instance.disposers.length; i++) {
      instance.disposers[i]();
    }

    instances.delete(root);
    var index = roots.indexOf(root);
    if (index !== -1) roots.splice(index, 1);
  }

  function init(scope) {
    var context = scope && scope.nodeType === 1 ? scope : document;

    if (context.matches && context.matches('[data-fbvc]')) {
      setup(context);
    }

    var found = context.querySelectorAll ? context.querySelectorAll('[data-fbvc]') : [];
    for (var i = 0; i < found.length; i++) {
      setup(found[i]);
    }

    syncAllVisibleVideos();
  }

  function destroyScope(scope) {
    var context = scope && scope.nodeType === 1 ? scope : document;
    var targets = [];

    if (context.matches && context.matches('[data-fbvc]')) {
      targets.push(context);
    }

    var found = context.querySelectorAll ? context.querySelectorAll('[data-fbvc]') : [];
    for (var i = 0; i < found.length; i++) {
      targets.push(found[i]);
    }

    for (var j = 0; j < targets.length; j++) {
      destroyRoot(targets[j]);
    }
  }

  window.CentauriFBVC = {
    init: init,
    destroy: destroyScope
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  window.addEventListener('resize', scheduleVisibleSync);
  window.addEventListener('orientationchange', scheduleVisibleSync);

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroyScope(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    init(event.target);
  });
})();
